import { ArrowLeft, Check, FileText, Loader2, X as XIcon } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useConfirmDialog } from "../hooks/useConfirmDialog";
import {
	approveApplication,
	getApplicationAdmin,
	rejectApplication,
} from "../services/api/applicationsApi";
import type { Application } from "../types/onboarding";
import { STATUS_COLORS, STATUS_LABELS } from "../types/onboarding";
import { notifications } from "../utils/notifications";

/** Read-only field display. */
const Field: React.FC<{
	label: string;
	value?: string | boolean | string[] | null;
}> = ({ label, value }) => {
	let display: string;
	if (value === undefined || value === null || value === "") {
		display = "\u2014";
	} else if (typeof value === "boolean") {
		display = value ? "Yes" : "No";
	} else if (Array.isArray(value)) {
		display = value.length > 0 ? value.join(", ") : "\u2014";
	} else {
		display = String(value);
	}

	return (
		<div
			style={{
				display: "grid",
				gridTemplateColumns: "12rem 1fr",
				padding: "0.4rem 0",
				borderBottom: "1px solid var(--color-border)",
			}}
		>
			<span
				style={{ fontSize: "0.8rem", color: "var(--color-text-secondary)" }}
			>
				{label}
			</span>
			<span style={{ fontSize: "0.8rem", color: "var(--color-text-primary)" }}>
				{display}
			</span>
		</div>
	);
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
	title,
	children,
}) => (
	<div style={{ marginBottom: "1.5rem" }}>
		<h3
			style={{
				fontSize: "0.875rem",
				fontWeight: 600,
				color: "var(--color-text-primary)",
				marginBottom: "0.5rem",
				textTransform: "uppercase",
				letterSpacing: "0.05em",
			}}
		>
			{title}
		</h3>
		<div style={{ display: "flex", flexDirection: "column" }}>{children}</div>
	</div>
);

const ApplicationDetail: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { confirm } = useConfirmDialog();
	const [application, setApplication] = useState<Application | null>(null);
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState(false);
	const [rejectNotes, setRejectNotes] = useState("");
	const [showRejectForm, setShowRejectForm] = useState(false);

	const fetchApplication = useCallback(async () => {
		if (!id) return;
		try {
			const app = (await getApplicationAdmin(id)) as unknown as Application;
			setApplication(app);
		} catch (err) {
			console.error("Failed to load application:", err);
			notifications.error("Failed to load application.");
		} finally {
			setLoading(false);
		}
	}, [id]);

	useEffect(() => {
		fetchApplication();
	}, [fetchApplication]);

	const handleApprove = async () => {
		if (!id || !application) return;
		const confirmed = await confirm({
			title: "Approve Application",
			message: `This will create a wallet, KYB form, and user account for ${application.company_legal_name || application.company_name}. Proceed?`,
			confirmLabel: "Approve",
		});
		if (!confirmed) return;

		setActionLoading(true);
		try {
			const result = await approveApplication(id);
			notifications.success(
				`Application approved! Wallet ID: ${result.wallet_id}`,
			);
			await fetchApplication();
		} catch (err) {
			console.error("Approval failed:", err);
			notifications.error("Approval failed. Check application for details.");
			await fetchApplication();
		} finally {
			setActionLoading(false);
		}
	};

	const handleReject = async () => {
		if (!id || !rejectNotes.trim()) {
			notifications.error("Please provide rejection notes.");
			return;
		}

		setActionLoading(true);
		try {
			await rejectApplication(id, rejectNotes);
			notifications.success("Application rejected.");
			setShowRejectForm(false);
			setRejectNotes("");
			await fetchApplication();
		} catch (err) {
			console.error("Rejection failed:", err);
			notifications.error("Rejection failed. Please try again.");
		} finally {
			setActionLoading(false);
		}
	};

	if (loading) {
		return (
			<div
				style={{ display: "flex", justifyContent: "center", padding: "3rem" }}
			>
				<Loader2
					size={32}
					style={{
						animation: "spin 1s linear infinite",
						color: "var(--color-primary)",
					}}
				/>
			</div>
		);
	}

	if (!application) {
		return (
			<div
				style={{
					textAlign: "center",
					padding: "3rem",
					color: "var(--color-text-secondary)",
				}}
			>
				Application not found.
			</div>
		);
	}

	const canApprove = ["submitted", "under_review", "approval_failed"].includes(
		application.status,
	);
	const canReject = ["submitted", "under_review"].includes(application.status);

	return (
		<div
			style={{
				padding: "1.5rem",
				paddingTop: "5rem",
				maxWidth: "56rem",
				margin: "0 auto",
			}}
		>
			{/* Header */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: "1rem",
					marginBottom: "1.5rem",
				}}
			>
				<Button variant="ghost" onClick={() => navigate("/applications")}>
					<ArrowLeft size={16} />
				</Button>
				<div style={{ flex: 1 }}>
					<h1
						style={{
							fontSize: "1.25rem",
							fontWeight: 600,
							color: "var(--color-text-primary)",
						}}
					>
						{application.company_legal_name ||
							application.company_name ||
							"Untitled Application"}
					</h1>
					<span
						style={{
							display: "inline-block",
							padding: "0.15rem 0.5rem",
							borderRadius: "1rem",
							fontSize: "0.75rem",
							fontWeight: 500,
							color: STATUS_COLORS[application.status],
							background: `color-mix(in srgb, ${STATUS_COLORS[application.status]} 12%, transparent)`,
							marginTop: "0.25rem",
						}}
					>
						{STATUS_LABELS[application.status]}
					</span>
				</div>

				{/* Action Buttons */}
				<div style={{ display: "flex", gap: "0.5rem" }}>
					{canReject && (
						<Button
							variant="outline"
							disabled={actionLoading}
							onClick={() => setShowRejectForm(!showRejectForm)}
							style={{
								color: "var(--color-error-500)",
								borderColor: "var(--color-error-500)",
							}}
						>
							<XIcon size={14} style={{ marginRight: "0.25rem" }} />
							Reject
						</Button>
					)}
					{canApprove && (
						<Button disabled={actionLoading} onClick={handleApprove}>
							{actionLoading ? (
								<Loader2
									size={14}
									style={{
										marginRight: "0.25rem",
										animation: "spin 1s linear infinite",
									}}
								/>
							) : (
								<Check size={14} style={{ marginRight: "0.25rem" }} />
							)}
							{application.status === "approval_failed"
								? "Retry Approve"
								: "Approve"}
						</Button>
					)}
				</div>
			</div>

			{/* Reject Form */}
			{showRejectForm && (
				<div
					style={{
						padding: "1rem",
						border: "1px solid var(--color-error-500)",
						borderRadius: "var(--border-radius)",
						marginBottom: "1.5rem",
						background: "var(--color-bg-secondary)",
					}}
				>
					<label
						style={{
							fontSize: "0.85rem",
							fontWeight: 500,
							color: "var(--color-text-primary)",
							display: "block",
							marginBottom: "0.5rem",
						}}
					>
						Rejection Notes *
					</label>
					<textarea
						value={rejectNotes}
						onChange={(e) => setRejectNotes(e.target.value)}
						rows={3}
						placeholder="Explain why this application is being rejected..."
						style={{
							width: "100%",
							padding: "0.5rem 0.75rem",
							borderRadius: "var(--border-radius)",
							border: "1px solid var(--color-border)",
							background: "var(--color-white)",
							color: "var(--color-text-primary)",
							fontSize: "0.85rem",
							fontFamily: "inherit",
							resize: "vertical",
						}}
					/>
					<div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
						<Button variant="outline" onClick={() => setShowRejectForm(false)}>
							Cancel
						</Button>
						<Button
							onClick={handleReject}
							disabled={actionLoading || !rejectNotes.trim()}
							style={{ background: "var(--color-error-500)" }}
						>
							{actionLoading ? (
								<>
									<Loader2
										size={14}
										style={{
											marginRight: "0.25rem",
											animation: "spin 1s linear infinite",
										}}
									/>
									Rejecting...
								</>
							) : (
								"Confirm Rejection"
							)}
						</Button>
					</div>
				</div>
			)}

			{/* Approval Result */}
			{application.status === "approved" && application.approved_wallet_id && (
				<div
					style={{
						padding: "1rem",
						border: "1px solid var(--color-success)",
						borderRadius: "var(--border-radius)",
						marginBottom: "1.5rem",
						background:
							"color-mix(in srgb, var(--color-success) 5%, var(--color-white))",
					}}
				>
					<h4
						style={{
							fontSize: "0.85rem",
							fontWeight: 600,
							marginBottom: "0.5rem",
							color: "var(--color-success)",
						}}
					>
						Approval Result
					</h4>
					<Field label="Wallet ID" value={application.approved_wallet_id} />
					{application.approved_user_id && (
						<Field label="User ID" value={application.approved_user_id} />
					)}
					{application.reviewed_by && (
						<Field label="Approved By" value={application.reviewed_by} />
					)}
					{application.reviewed_at && (
						<Field
							label="Approved At"
							value={new Date(application.reviewed_at).toLocaleString()}
						/>
					)}
				</div>
			)}

			{/* Approval Error */}
			{application.status === "approval_failed" && (
				<div
					style={{
						padding: "1rem",
						border: "1px solid var(--color-error-500)",
						borderRadius: "var(--border-radius)",
						marginBottom: "1.5rem",
						background:
							"color-mix(in srgb, var(--color-error-500) 5%, var(--color-white))",
					}}
				>
					<h4
						style={{
							fontSize: "0.85rem",
							fontWeight: 600,
							marginBottom: "0.5rem",
							color: "var(--color-error-500)",
						}}
					>
						Approval Failed
					</h4>
					<p
						style={{ fontSize: "0.8rem", color: "var(--color-text-secondary)" }}
					>
						The approval process encountered an error. You can retry using the
						Approve button.
					</p>
					{application.approved_wallet_id && (
						<Field
							label="Wallet Created"
							value={application.approved_wallet_id}
						/>
					)}
				</div>
			)}

			{/* Rejection Notes */}
			{application.status === "rejected" && application.reviewer_notes && (
				<div
					style={{
						padding: "1rem",
						border: "1px solid var(--color-border)",
						borderRadius: "var(--border-radius)",
						marginBottom: "1.5rem",
						background: "var(--color-bg-secondary)",
					}}
				>
					<h4
						style={{
							fontSize: "0.85rem",
							fontWeight: 600,
							marginBottom: "0.5rem",
						}}
					>
						Rejection Notes
					</h4>
					<p
						style={{
							fontSize: "0.85rem",
							color: "var(--color-text-secondary)",
						}}
					>
						{application.reviewer_notes}
					</p>
				</div>
			)}

			{/* Application Data */}
			<Section title="Company Information">
				<Field label="Company Name" value={application.company_name} />
				<Field label="Wallet Name" value={application.wallet_name} />
				<Field label="Custodial Type" value={application.custodial_type} />
				<Field label="Chain" value={application.chain} />
				<Field label="Asset" value={application.asset} />
				<Field label="Products" value={application.products} />
				<Field label="Country" value={application.country_of_incorporation} />
				<Field label="Website" value={application.website} />
				<Field label="Description" value={application.business_description} />
			</Section>

			<Section title="Contact Details">
				<Field label="Name" value={application.primary_contact?.name} />
				<Field label="Email" value={application.primary_contact?.email} />
				<Field label="Phone" value={application.primary_contact?.phone} />
				<Field label="Role" value={application.primary_contact?.role} />
				{application.secondary_contact?.name && (
					<>
						<div
							style={{
								borderTop: "1px solid var(--color-border)",
								margin: "0.25rem 0",
							}}
						/>
						<Field
							label="Secondary Name"
							value={application.secondary_contact.name}
						/>
						<Field
							label="Secondary Email"
							value={application.secondary_contact.email}
						/>
					</>
				)}
			</Section>

			<Section title="Wallet Configuration">
				<Field label="Wallet Name" value={application.wallet_name} />
				<Field label="Custodial Type" value={application.custodial_type} />
				<Field label="Chain" value={application.chain} />
				<Field label="Asset" value={application.asset} />
				<Field label="Products" value={application.products} />
				<Field label="Est. MAU" value={application.estimated_mau_range} />
				<Field label="Conducts KYC" value={application.conducts_kyc} />
			</Section>

			<Section title="Compliance">
				<Field label="Regulated" value={application.is_regulated} />
				<Field label="Licenses" value={application.licenses} />
				<Field label="Jurisdictions" value={application.jurisdictions} />
				{application.compliance_officer?.name && (
					<Field
						label="Compliance Officer"
						value={`${application.compliance_officer.name} (${application.compliance_officer.email})`}
					/>
				)}
			</Section>

			<Section title="Documents">
				{application.documents && application.documents.length > 0 ? (
					application.documents.map((doc) => (
						<div
							key={doc.s3Key}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "0.5rem",
								padding: "0.4rem 0",
								borderBottom: "1px solid var(--color-border)",
							}}
						>
							<FileText size={14} style={{ color: "var(--color-success)" }} />
							<span
								style={{
									fontSize: "0.8rem",
									color: "var(--color-text-primary)",
								}}
							>
								{doc.fileName}
							</span>
							<span
								style={{
									fontSize: "0.7rem",
									color: "var(--color-text-secondary)",
									marginLeft: "auto",
								}}
							>
								{doc.type}
							</span>
						</div>
					))
				) : (
					<span
						style={{ fontSize: "0.8rem", color: "var(--color-text-secondary)" }}
					>
						No documents
					</span>
				)}
			</Section>

			<Section title="Metadata">
				<Field label="Application ID" value={application.id} />
				<Field
					label="Created"
					value={new Date(application.created_at).toLocaleString()}
				/>
				<Field
					label="Updated"
					value={new Date(application.updated_at).toLocaleString()}
				/>
				{application.submitted_at && (
					<Field
						label="Submitted"
						value={new Date(application.submitted_at).toLocaleString()}
					/>
				)}
				{application.notification_status && (
					<Field
						label="Notification"
						value={
							typeof application.notification_status === "string"
								? application.notification_status
								: "configured"
						}
					/>
				)}
			</Section>
		</div>
	);
};

export default ApplicationDetail;
